import { Avatar, TableColumnsType, Tooltip, Typography } from "antd";
import {
  decryptData,
  getInitials,
  convertToCustomFormat,
} from "../../../utils";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { formatRupee } from "../../../utils";

export type AdminCasesTableColumnsTypes = {
  loanId: React.ReactNode;
  key: React.Key;
  borrowerName: React.ReactNode;
  nbfc: React.ReactNode;
  dpdStatus: React.ReactNode;
  loanAmount: React.ReactNode;
  dueAmount: React.ReactNode;
  status: React.ReactNode;
  lastCallStatus: React.ReactNode;
  bucketName: string;
};

interface PhonebookTableColumnsTypes {
  totalLoanId: number;
  invalidCount: number;
  duplicatesCount: number;
  ndncCount: number;
  id: React.Key;
  phonebookname: React.ReactNode;
  timestamp: React.ReactNode;
  failedCount: React.ReactNode;
  successCount: React.ReactNode;
  invalidUrl: string;
  totalDueAmount: number;
}

export interface BorrowerStatsTableTypes {
  dpdBucket: string;
  originalPrincipal: number;
  outstandingValue: number;
  totalBorrower: number;
  min: number;
  max: number;
  average: number;
  median: number;
  agentCallsMade: number;
  ivrCalls: number;
  borrowersCount: number;
  demandNotices: number;
  emailSent: number;
  smsSent: number;
  legalNotices: number;
  mediationNotices: number;
  recoveredAmount: number;
  totalOsAmount?: number;
}

const handleDownloadFailed = (record: PhonebookTableColumnsTypes) => {
  const fileUrl = record.invalidUrl;
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = "failed_phonebook.xlsx"; // Optional: set default filename
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const adminCasesTableColumn: TableColumnsType<AdminCasesTableColumnsTypes> =
  [
    {
      title: "Loan ID",
      dataIndex: "loanId",
      render: (id) => <span>{id || "-"}</span>,
    },
    {
      title: "Borrower Name",
      dataIndex: "borrowerName",
      sorter: (a: any, b: any) => a.borrowerName,
      render: (name) => {
        const decryptedName = decryptData(name);
        return (
          <Tooltip title={decryptedName || "-"}>
            <span className="flex flex-wrap" style={{ maxWidth: "150px" }}>
              {decryptedName || "-"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Lender",
      dataIndex: "clientName",
      render: (clientName) => <span>{clientName || "-"}</span>,
    },
    {
      title: "DPD Status",
      dataIndex: "dpdStatus",
      sorter: (a: any, b: any) => 0,
      render: (dpdStatus) => {
        let bg = "";
        let status = "";
        if (dpdStatus === "DPD 0-30") {
          bg = "#6aa84f";
          status = "0-30";
        } else if (dpdStatus === "DPD 60-90") {
          bg = "#f1c232";
          status = "60-90";
        } else if (dpdStatus === "DPD 30-60") {
          bg = "#3d85c6";
          status = "30-60";
        } else {
          bg = "#cc0000";
          status = "90+";
        }

        return (
          <Tooltip title={dpdStatus || "-"}>
            <Typography.Text
              style={{
                background: bg,
                padding: status === "0-30" ? ".25rem 0.5rem" : " .25rem 0.5rem",
                borderRadius: "10px",
                maxWidth: "1rem",
                color: "white",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              {status || <span style={{ color: "gray" }}> -</span>}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Loan Amount",
      dataIndex: "loanAmount",
      sorter: (a: any, b: any) => 0,
      render: (loanAmount) => (
        <span>{loanAmount ? ` ${formatRupee(loanAmount)}` : "-"}</span>
      ),
    },
    {
      title: "Due Amount",
      dataIndex: "dueAmount",
      sorter: (a: any, b: any) => 0,
      render: (dueAmount) => (
        <span>{dueAmount ? `${formatRupee(dueAmount)}` : "-"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tooltip title={status || "-"}>{status || "-"}</Tooltip>
      ),
    },
    {
      title: "Agent",
      dataIndex: "assignee",
      render: (agentName) => {
        const initials = getInitials(agentName);
        return agentName === "NA" || agentName === "" ? (
          <span>-</span>
        ) : (
          <Tooltip title={agentName || "Not Available"}>
            <Avatar style={{ backgroundColor: "#fde3cf", color: "#f56a00" }}>
              {initials}
            </Avatar>
          </Tooltip>
        );
      },
    },
    {
      title: "Last Call Date",
      dataIndex: "lastCallDate",
      render: (lastCallDate) => <span>{lastCallDate || "N/A"}</span>,
    },
    {
      title: "Next Call Date",
      dataIndex: "nextScheduledCallDate",
      render: (nextScheduledCallDate) => (
        <span>{nextScheduledCallDate || "N/A"}</span>
      ),
    },
  ];

export const phonebookTableColumn: TableColumnsType<PhonebookTableColumnsTypes> =
  [
    {
      title: "NAME",
      dataIndex: "phonebookname",
      render: (phonebookname) => <span>{phonebookname || "-"}</span>,
    },
    {
      title: "PHONEBOOK ID",
      dataIndex: "id",
      render: (id) => <span>{id || "-"}</span>,
    },
    {
      title: "CREATED ON",
      dataIndex: "timestamp",
      render: (timestamp) => (
        <span>{convertToCustomFormat(timestamp) || "-"}</span>
      ),
    },
    {
      title: "COUNT",
      dataIndex: "totalLoanId",
      render: (totalLoanId: number, record: PhonebookTableColumnsTypes) => (
        <span>
          Total Uploaded: {totalLoanId} |{"  "}
          <CheckCircleOutlined className="text-green-500  align-right" />{" "}
          Success: {record.successCount} |{"  "}
          <CloseCircleOutlined className="text-red-500  align-middle" /> Failed:{" "}
          {record.failedCount}
          <DownloadOutlined
            className="text-blue-500 text-xs align-middle ml-1 cursor-pointer"
            onClick={() => handleDownloadFailed(record)}
          />
          <br />
          <span>
            Total Due Amount:{" "}
            {record?.totalDueAmount ? `₹  ${record?.totalDueAmount}` : "-"}
          </span>
          <br />
          <span>
            DND/NDNC: {record?.ndncCount || "NA"} | Invalid:{" "}
            {record?.invalidCount} | Duplicate: {record?.duplicatesCount}
          </span>
        </span>
      ),
    },
  ];

  const baseColumns: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "ORIGINAL PRINCIPAL",
    dataIndex: "originalPrincipal",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  {
    title: "OUTSTANDING VALUE",
    dataIndex: "outstandingValue",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  {
    title: "TOTAL BORROWER",
    dataIndex: "totalBorrower",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "MIN",
    dataIndex: "min",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  {
    title: "MAX",
    dataIndex: "max",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  {
    title: "AVERAGE",
    dataIndex: "average",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  {
    title: "MEDIAN",
    dataIndex: "median",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
];


export const preDpdTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "DPD BUCKET",
    dataIndex: "dpdBucket",
    render: (dpdBucket: string) => <span>{dpdBucket || "-"}</span>,
  },
  ...baseColumns,
];



export const prePrincipalPaidTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "PRINCIPAL PAID",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...baseColumns,
];


export const preGenderTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Gender",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...baseColumns,
];

export const preFirstVsRepeatTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Borrower Type",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...baseColumns,
];

export const preQuartileTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Quartile Buckets",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...baseColumns,
];

const dailybaseColumns: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Agent Calls Made",
    dataIndex: "agentCallsMade",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "IVR Calls",
    dataIndex: "ivrCalls",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "No. Of Borrowers",
    dataIndex: "borrowersCount",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "Dunning Notices",
    dataIndex: "demandNotices",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "Email Sent",
    dataIndex: "emailSent",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "SMS Sent",
    dataIndex: "smsSent",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "Legal Notices",
    dataIndex: "legalNotices",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "Mediation Notices",
    dataIndex: "mediationNotices",
    render: (value: number) => <span>{value || "-"}</span>,
  },
  {
    title: "Recovered Amount",
    dataIndex: "recoveredAmount",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
    {
    title: "MTD Collections (₹)",
    dataIndex: "recoveredAmount",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
   {
    title: "% Recovery vs OS",
    dataIndex: "recoveredAmount",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
];

export const dailyDpdTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "DPD BUCKET",
    dataIndex: "dpdBucket",
    render: (dpdBucket: string) => <span>{dpdBucket || "-"}</span>,
  },
  ...dailybaseColumns,
];

export const dailyPrincipalPaidTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "PRINCIPAL PAID",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...dailybaseColumns,
];

export const dailyGenderTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Gender",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...dailybaseColumns,
];

export const dailyFirstVsRepeatTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Borrower Type",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...dailybaseColumns,
];

export const dailyQuartileTableColumn: TableColumnsType<BorrowerStatsTableTypes> = [
  {
    title: "Quartile Buckets",
    dataIndex: "principalPaid",
    render: (value: number) => <span>{value ? `₹ ${value}` : "-"}</span>,
  },
  ...dailybaseColumns,
];



  